import React, { useEffect } from "react";
import clsx from "clsx";
import { ExternalLink } from "lucide-react";
import {
  Button,
  ButtonVariant,
  Card,
  SkeletonLoader,
  Text,
} from "@merge-api/merge-javascript-shared";
import { MERGE_COMMON_MODEL_SCOPES_HELP_CENTER } from "../../../../../../router/RouterUtils";
import { EnabledAction, ScopeOption } from "../../../../../../models/CommonModel";
import { CUSTOM_OBJECT_COMMON_MODEL_NAMES } from "../../../../../../constants";
import { APICategory } from "../../../../../../models/Entities";
import useAppContext from "../../../../../context/useAppContext";
import EmptyStateWrapper from "../../../../../shared/EmptyStateWrapper";
import { SortedModelsMap } from "../../CommonModelUtils";
import {
  REMOTE_DATA_FIELD,
  REMOTE_FIELD_CLASS_MODEL,
  SCOPE_COLUMN_CLASS,
  SCOPE_TABLE_MIN_WIDTH_CLASS,
} from "../constants";
import { isModelHidden } from "../utils/deprecation";
import ScopeObjectRow from "./ScopeObjectRow";

interface Props {
  models: SortedModelsMap;
  /** Last-saved model state; deprecated-item visibility keys off this, not the in-flight edit, so a
      row doesn't vanish the instant you toggle it off (it drops out once the save persists). */
  savedModels: SortedModelsMap;
  /** Server-ordered/filtered model names to render (from the toolbar), or null to show all models in
      their default order. Only the object display order/inclusion is driven by this; the full models
      map is still used for relations and edits. */
  orderedModelNames?: string[] | null;
  /** True while the toolbar's filter/search fetch is in flight (or failed); shows skeleton rows. */
  resultsLoading?: boolean;
  category: APICategory;
  isViewing: boolean;
  areOptionalScopesEnabled: boolean;
  disableObjectScopes: boolean;
  disableFieldScopes: boolean;
  disableRemoteScopes: boolean;
  objectScopesDisabledTooltip?: React.ReactNode;
  fieldScopesDisabledTooltip?: React.ReactNode;
  remoteScopesDisabledTooltip?: React.ReactNode;
  onModelScopeChange: (modelName: string, capability: EnabledAction, scope: ScopeOption) => void;
  onFieldScopeChange: (modelName: string, fieldName: string, scope: ScopeOption) => void;
}

/** Bordered table of Common Model scopes; one expandable accordion row per top-level object. */
const ScopesTable = ({
  models,
  savedModels,
  orderedModelNames,
  resultsLoading,
  category,
  isViewing,
  areOptionalScopesEnabled,
  disableObjectScopes,
  disableFieldScopes,
  disableRemoteScopes,
  objectScopesDisabledTooltip,
  fieldScopesDisabledTooltip,
  remoteScopesDisabledTooltip,
  onModelScopeChange,
  onFieldScopeChange,
}: Props) => {
  // hooks
  const { showDeprecatedSunsetItems } = useAppContext();

  // effects
  // On mount (table renders only once data has loaded, so the row ids exist), scroll to the object
  // named in the URL hash, e.g. landing on .../scopes/hris#EmployerBenefit.
  useEffect(() => {
    const id = window.location.hash.slice(1);
    if (!id) return;
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, []);

  // derived state
  // When the toolbar is active, render the server-returned models in the server's order; otherwise
  // fall back to every loaded model in its default order.
  const source =
    orderedModelNames != null
      ? orderedModelNames.map((name) => models[name]).filter(Boolean)
      : Object.values(models);
  const objects = source
    .filter((model) => model.has_endpoints)
    .filter((model) => !CUSTOM_OBJECT_COMMON_MODEL_NAMES.includes(model.name))
    .filter((model) => {
      // Visibility keys off the saved enabled state, not the in-flight edit.
      const savedModel = savedModels[model.name] ?? model;
      const isEnabled = savedModel.enabled_model_actions.length > 0;
      return !isModelHidden(category, model.name, isEnabled, !!showDeprecatedSunsetItems);
    });

  // render
  return (
    <Card className="overflow-hidden">
      <div className="overflow-x-auto">
        <div className={SCOPE_TABLE_MIN_WIDTH_CLASS}>
          <div className="flex items-center border-b border-gray-10 px-4 py-3">
            <div className={SCOPE_COLUMN_CLASS.object}>
              <Text variant="pre-title">Object</Text>
            </div>
            <div className={SCOPE_COLUMN_CLASS.read}>
              <Text variant="pre-title">Read permissions</Text>
            </div>
            <div className={SCOPE_COLUMN_CLASS.write}>
              <Text variant="pre-title">Write permissions</Text>
            </div>
            {/* Unlabeled spacer aligning with the per-row deprecated-badge column. */}
            <div className={SCOPE_COLUMN_CLASS.status} />
            <div className={clsx(SCOPE_COLUMN_CLASS.fields, "text-left")}>
              <Text variant="pre-title">Fields</Text>
            </div>
            {/* Docs link in its own column, aligned above the per-row expand chevrons. */}
            <div className={clsx(SCOPE_COLUMN_CLASS.expand, "flex items-center justify-end")}>
              <Button
                variant={ButtonVariant.TextBlue}
                size="sm"
                onClick={() =>
                  window.open(
                    MERGE_COMMON_MODEL_SCOPES_HELP_CENTER,
                    "_blank",
                    "noopener,noreferrer",
                  )
                }
                rightIcon={<ExternalLink size={12} />}
                className="shrink-0"
              >
                Learn more in docs
              </Button>
            </div>
          </div>
          {resultsLoading &&
            Array.from({ length: 8 }).map((_, index) => (
              <div
                key={`skeleton-${index}`}
                className={clsx(
                  "flex items-center px-4 py-3",
                  index > 0 && "border-t border-gray-10",
                )}
              >
                <SkeletonLoader fullWidth height={24} borderRadius={8} className="block w-full" />
              </div>
            ))}
          {!resultsLoading && objects.length === 0 && (
            <EmptyStateWrapper
              isTable
              title={
                orderedModelNames != null
                  ? "No objects match your filters"
                  : "No objects to display"
              }
            />
          )}
          {!resultsLoading &&
            objects.map((model, index) => (
              <ScopeObjectRow
                key={model.name}
                model={model}
                models={models}
                savedModels={savedModels}
                category={category}
                showDeprecatedSunsetItems={!!showDeprecatedSunsetItems}
                isFirst={index === 0}
                isViewing={isViewing}
                areOptionalScopesEnabled={areOptionalScopesEnabled}
                disableObjectScopes={disableObjectScopes}
                disableFieldScopes={disableFieldScopes}
                disableRemoteScopes={disableRemoteScopes}
                objectScopesDisabledTooltip={objectScopesDisabledTooltip}
                fieldScopesDisabledTooltip={fieldScopesDisabledTooltip}
                remoteScopesDisabledTooltip={remoteScopesDisabledTooltip}
                onModelScopeChange={(capability, scope) =>
                  onModelScopeChange(model.name, capability, scope)
                }
                onFieldScopeChange={onFieldScopeChange}
                onRemoteDataChange={(scope) =>
                  onFieldScopeChange(model.name, REMOTE_DATA_FIELD, scope)
                }
                onRemoteFieldsChange={(scope) =>
                  onModelScopeChange(REMOTE_FIELD_CLASS_MODEL, EnabledAction.READ, scope)
                }
              />
            ))}
        </div>
      </div>
    </Card>
  );
};

export default ScopesTable;
